import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadCounter from 'reactApp/components/DownloadCounter/DownloadCounter';
import { useIsTutoriaIntegration } from 'reactApp/hooks/useIsTutoriaIntegration';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getIntegrationClient } from 'reactApp/modules/integration/integration.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getFirstPublicWithDownloadLimits, getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { DataListControl } from 'reactApp/ui/DataListControl/DataListControl';
import { DatalistPic } from 'reactApp/ui/Mobile/Datalist/DatalistPic/DatalistPic';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { TrackDownloadCountPromo } from 'reactApp/ui/SharingTooltips/TrackDownloadCountPromo';
import { Icon20ChainOutline, Icon20MoreDots } from 'reactApp/ui/touchIcons';
import { noop } from 'reactApp/utils/helpers';

import styles from './DatalistThumbItem.css';
import type { IProps } from './DatalistThumbItem.types';

export const DatalistThumbItem = memo(
    ({
        isFolder,
        kind,
        thumbUrl,
        ext,
        isVirus,
        isSelected,
        isSelecting,
        name,
        weblink,
        onClick,
        onDoubleClick,
        onOptions,
        onShare,
        id,
        author,
        onQuotaCleanerSelect,
        moreBtnRef,
        hideItemActions,
    }: IProps): ReactElement => {
        const [isLoaded, setLoaded] = useState<boolean>(false);

        const onLoaded = useCallback(() => setLoaded(true), []);

        const storage = useSelector(getCurrentStorage);
        const { isTutoria } = useSelector(getIntegrationClient);
        const { isPublic, isQuotaCleaner, isIntegration } = getStorage(storage);

        const isTutoriaIntegration = useIsTutoriaIntegration();

        // tempexp_17847-start
        const tooltipRef = useRef<HTMLDivElement>(null);
        const [showTooltipCounter, setShowTooltipCounter] = useState(false);
        const TOOLTIP_ID = 'tooltipTrackDownload';
        const isTooltipShown = Boolean(storeHelper.getValue(TOOLTIP_ID));
        const isPaidUser = useSelector(UserSelectors.isPaidUser);
        const routeId = useSelector(getCurrentRouteId);
        const weblinkUrl = getWeblinkFromPublicId(routeId);
        const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || (weblink as string) || ''));
        const downloadTotal = downloadLimits && downloadLimits.total;
        const downloadLeft = downloadLimits && downloadLimits.left;
        const downloads = downloadTotal - downloadLeft;
        const showDownloadCounter = enabledPublicDownloadLimits && storage === EStorageType.home && !isPaidUser && downloads > 0;

        const firstPublicWithDownloads = useSelector((state: RootState) => getFirstPublicWithDownloadLimits(state));
        const weblinkForPromo = firstPublicWithDownloads && Object.keys(firstPublicWithDownloads)[0];
        const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;
        const showFirstTrackDownloadCountPromo = showTrackDownloadCountPromo && weblink === weblinkForPromo;

        const handleCounterClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowTooltipCounter(true);
        };

        const handleCounterPromoHide = () => {
            setShowTooltipCounter(false);
        };
        // tempexp_17847-end

        return (
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_loaded]: isLoaded || isFolder || !thumbUrl?.src,
                    [styles.root_selecting]: isSelecting,
                    [styles.root_selected]: isSelected,
                    [styles.root_inlineIntegration]: isTutoriaIntegration,
                    [styles.root_inlineIntegration_selected]: isSelected && isTutoriaIntegration,
                })}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                data-qa-id={id}
                data-qa-type={isFolder ? 'folder' : 'file'}
                data-qa-name={ext ? `${name}.${ext}` : name}
            >
                <div className={styles.content}>
                    <div className={styles.check} onClick={onQuotaCleanerSelect}>
                        <DataListControl type="check" active={isSelected} size={28} viewType={EViewMode.thumbs} />
                    </div>
                    <div className={styles.pic}>
                        <DatalistPic
                            isFolder={isFolder}
                            ext={ext || ''}
                            kind={kind}
                            thumbUrl={thumbUrl}
                            isVirus={isVirus}
                            viewMode={EViewMode.thumbs}
                            onLoaded={onLoaded}
                            author={author}
                        />
                    </div>
                    <div className={styles.name}>
                        <NameComponent name={name} extension={ext} truncate />
                    </div>

                    {!(isTutoria && isIntegration) && (
                        <div className={styles.buttons}>
                            {showDownloadCounter && (
                                <div ref={tooltipRef}>
                                    <DownloadCounter
                                        downloads={downloads}
                                        downloadLimit={downloadTotal}
                                        onClick={handleCounterClick}
                                        isMobile
                                    />
                                </div>
                            )}
                            {showFirstTrackDownloadCountPromo && !isTooltipShown && (
                                <TrackDownloadCountPromo
                                    targetRef={tooltipRef}
                                    downloads={downloads}
                                    downloadLimit={downloadTotal}
                                    isMobile
                                />
                            )}
                            {showTrackDownloadCountPromo && showTooltipCounter && (
                                <TrackDownloadCountPromo
                                    targetRef={tooltipRef}
                                    downloads={downloads}
                                    downloadLimit={downloadTotal}
                                    isMobile
                                    onHide={handleCounterPromoHide}
                                />
                            )}
                            {!hideItemActions && !isPublic && !!weblink && (
                                <div className={styles.button_publish} onClick={isSelecting ? noop : onShare}>
                                    <Icon20ChainOutline className={styles.icon} width={20} height={20} />
                                </div>
                            )}
                            {!hideItemActions && !isQuotaCleaner && (
                                <div ref={moreBtnRef} className={styles.button_more} onClick={isSelecting ? noop : onOptions}>
                                    <Icon20MoreDots className={styles.icon} width={20} height={20} fill="currentColor" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

DatalistThumbItem.displayName = 'DatalistThumbItem';
